import { useParams, Link } from 'react-router-dom';
import { allArticles, categories } from '@/mocks/data';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);
  const relatedArticles = allArticles
    .filter((a) => a.category === article?.category && a.slug !== slug)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-6 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    url: `https://17c.com/article/${article.slug}`,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '17c',
      url: 'https://17c.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://17c.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        schemaJson={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full max-w-[900px] mx-auto px-6 md:px-10 py-10 md:py-14">
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-600 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500">
                  #{tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-5 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                {article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&>h2]:text-xl [&>h2]:md:text-2xl [&>h2]:font-heading [&>h2]:font-bold [&>h2]:text-foreground-950 [&>h2]:mt-10 [&>h2]:mb-4
              [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-foreground-700
              [&>h3]:text-lg [&>h3]:font-heading [&>h3]:font-semibold [&>h3]:text-foreground-900 [&>h3]:mt-8 [&>h3]:mb-3"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-14 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-32 rounded-md overflow-hidden mb-3">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-1.5 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <span className="text-xs text-foreground-400">{related.readTime}</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}